import React, { useState } from 'react';
import { Eye, CheckCircle, XCircle, MessageSquare, Loader2, BellRing } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const countryFlags = { CO: '🇨🇴', VE: '🇻🇪', US: '🇺🇸', PE: '🇵🇪', PA: '🇵🇦', EC: '🇪🇨' };

const STATUS_COLORS = {
  Procesando: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  Completado: 'bg-green-50 text-green-700 border-green-200',
  Cancelada: 'bg-red-50 text-red-700 border-red-200',
  Creando: 'bg-gray-50 text-gray-700 border-gray-200',
  'En pago': 'bg-blue-50 text-blue-700 border-blue-200'
};

export default function TransactionsTable({ transactions, onOpenDetails, onRefresh }) {
  const [page, setPage] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);
  const PAGE_SIZE = 12;

  const formatCurrency = (value, locale = 'es-CO', currency = 'USD') => {
    if (value == null) return '-';
    try { return Number(value).toLocaleString(locale, { style: 'currency', currency }); } 
    catch { return Number(value).toLocaleString(); }
  };

  const getFlag = (code) => countryFlags[code?.toUpperCase()] || '🌍';

  // Lógica para actualizar estado rápido desde la tabla (Ej: Botón PUSH o Procesando)
  const handleQuickUpdate = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await supabase.from('transactions').update({ 
        status: newStatus, 
        updated_at: new Date().toISOString() 
      }).eq('id', id);

      // Guardar auditoría
      await supabase.from('transactions_audit').insert({  
        transaction_id: id,  
        action: `Estado cambiado a "${newStatus}" desde Tabla`,  
        admin: 'admin_panel'
      });

      onRefresh(); // Refrescar la tabla padre
      if (newStatus !== 'Procesando') alert(`✔ Estado actualizado a "${newStatus}"`);
    } catch (error) {
      alert("Error actualizando estado.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handlePushAction = (id) => {
    alert("¡Atención! Entra a la app de Nequi/DaviPlata y envía el cobro PUSH al número del cliente.\n\nAl darle Aceptar, la orden pasará a 'Procesando'.");
    handleQuickUpdate(id, 'Procesando');
  };

  if (!transactions || transactions.length === 0) {
    return <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center shadow-sm text-slate-500 font-bold">No se encontraron transacciones en esta categoría.</div>;
  }

  const start = (page - 1) * PAGE_SIZE;
  const pageItems = transactions.slice(start, start + PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));

  return (
    <div className="bg-white shadow-sm rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">  
        <table className="w-full text-sm text-left">  
          <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 uppercase text-[10px] font-black tracking-widest">  
            <tr>  
              <th className="p-4">#</th>  
              <th className="p-4">Envío</th>  
              <th className="p-4">Usuario</th>  
              <th className="p-4">Monto → Recibe</th>  
              <th className="p-4">Fecha</th>  
              <th className="p-4">Estado</th>  
              <th className="p-4 text-right pr-6">Acciones</th>  
            </tr>  
          </thead>  
          <tbody className="divide-y divide-slate-100">  
            {pageItems.map((t) => {
              const isPushPending = t.current_step === 'confirm' && t.payment_methods_origin?.is_push;
              const isUpdating = updatingId === t.id;

              return (
                <tr key={t.id} className="hover:bg-slate-50/70 transition-colors">  
                  <td className="p-4 font-bold text-slate-600">{t.id}</td>  
                  <td className="p-4 flex items-center gap-2">  
                    <span className="text-xl">{getFlag(t.origin_country)}</span>  
                    <span className="font-bold">{t.origin_country}</span>  
                    <span className="text-slate-300">→</span>  
                    <span className="text-xl">{getFlag(t.destination_country)}</span>  
                    <span className="font-bold">{t.destination_country}</span>  
                  </td>  
                  <td className="p-4">  
                    <div className="font-bold text-slate-800">{t.user_name || t.user_id}</div>  
                    <div className="text-xs font-medium text-slate-500">{t.user_phone || '-'}</div>  
                  </td>  
                  <td className="p-4">  
                    <div className="font-black text-slate-800">  
                      {formatCurrency(t.amount, 'es-CO', t.origin_currency)} → <span className="text-green-600">{formatCurrency(t.received_amount, 'es-CO', t.destination_currency)}</span>
                    </div>  
                    <div className="text-[11px] font-bold text-slate-400 mt-0.5">Tasa: {t.rate}</div>  
                  </td>  
                  <td className="p-4 text-xs font-medium text-slate-500">{new Date(t.created_at).toLocaleString('es-CO')}</td>  
                  <td className="p-4">  
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md border text-xs font-bold uppercase tracking-wide ${STATUS_COLORS[t.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>  
                      {t.status === 'Completado' ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : t.status === 'Procesando' ? <Loader2 className="w-3.5 h-3.5 text-yellow-600 animate-spin" /> : <XCircle className="w-3.5 h-3.5 text-slate-500" />}  
                      <span>{t.status}</span>  
                    </div>  
                  </td>  
                  <td className="p-4 flex justify-end flex-wrap gap-2">  
                    <button className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-xs font-bold hover:bg-blue-100 transition shadow-sm" onClick={() => onOpenDetails(t)}>  
                      <Eye className="w-4 h-4" /> Ver  
                    </button>  

                    {isPushPending ? (
                      <button className="flex items-center gap-1.5 bg-red-100 text-red-700 border border-red-300 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-red-200 transition shadow-sm animate-pulse disabled:opacity-50" onClick={() => handlePushAction(t.id)} disabled={isUpdating}>  
                        {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} PUSH  
                      </button>
                    ) : (
                      <button className="flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 px-3 py-2 rounded-lg text-xs font-bold hover:bg-green-100 transition shadow-sm disabled:opacity-50" onClick={() => handleQuickUpdate(t.id, 'Procesando')} disabled={isUpdating || t.status === 'Procesando' || t.status === 'Completado'}>  
                        {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Procesar  
                      </button>
                    )}

                    <button className="flex items-center gap-1.5 bg-slate-100 text-slate-700 px-3 py-2 rounded-lg text-xs font-bold hover:bg-slate-200 transition shadow-sm" onClick={() => {  
                      const phone = t.phone || null;  
                      if (phone) window.open(`https://wa.me/${phone.replace(/\D/g, '')}`);  
                      else alert('No hay teléfono disponible');  
                    }}>  
                      <MessageSquare className="w-4 h-4" /> Chat  
                    </button>  
                  </td>  
                </tr>  
              );
            })}  
          </tbody>  
        </table>  
      </div>  

      {/* PAGINACIÓN */}  
      <div className="flex items-center justify-between p-4 border-t border-slate-100 bg-slate-50/50">  
        <div className="text-xs font-bold text-slate-500 tracking-wide uppercase">{transactions.length} Resultados</div>  
        <div className="flex items-center gap-2">  
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 disabled:opacity-40 hover:bg-slate-50 shadow-sm transition">Anterior</button>  
          <div className="px-4 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-black text-slate-700">{page} / {totalPages}</div>  
          <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 disabled:opacity-40 hover:bg-slate-50 shadow-sm transition">Siguiente</button>  
        </div>  
      </div>  
    </div>
  );
}

